import { motion } from "framer-motion";
import { Music2, Disc3, ExternalLink } from "lucide-react";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { TrackClips } from "@/components/TrackClips";

const CD_COVER =
  "https://customer-assets.emergentagent.com/job_rock-band-preview/artifacts/9yqwvlyn_image0.jpg";

export const Music = () => {
  // Settings come from the shared provider — no need for a second /settings fetch.
  const settings = useSiteSettings();

  const artistId = settings.spotify_artist_id || "7hHitkJMarKSdSUt028YQq";
  const embedUrl = `https://open.spotify.com/embed/artist/${artistId}?utm_source=generator&theme=0`;

  return (
    <section
      id="music"
      data-testid="music-section"
      className="relative bg-[#0F0808] py-24 md:py-32 px-6 md:px-16 border-t border-white/5 overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-[#B91C1C]/10 blur-3xl pointer-events-none" />

      <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start relative">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative grain-overlay overflow-hidden aspect-square bg-black">
            <img
              src={CD_COVER}
              data-testid="cd-cover-image"
              alt="Trapped in a Montana Winter album cover"
              className="absolute inset-0 w-full h-full object-cover shadow-2xl"
            />
            <div className="absolute top-4 left-4 bg-[#B91C1C] text-white font-oswald uppercase tracking-[0.2em] text-xs px-4 py-2 flex items-center gap-2 z-10">
              <Disc3 size={14} /> Out Now
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-7"
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="h-px w-10 bg-[#DC2626]" />
            <span className="font-oswald text-xs uppercase tracking-[0.3em] text-[#DC2626]">
              Debut Album · Nov 2024
            </span>
          </div>
          <h2 className="font-anton uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.88] mb-6">
            Trapped in a
            <br />
            <span className="text-[#B91C1C]">Montana Winter</span>
          </h2>
          <p className="text-white/70 font-manrope text-lg max-w-xl mb-8">
            {settings.music_note ||
              "Randy's first studio record — recorded cold, played hot. Spin the live catalog below, then catch a show."}
          </p>

          {/* Spotify embed */}
          <div
            data-testid="spotify-embed-wrapper"
            className="border-2 border-white/10 overflow-hidden bg-black"
            style={{ borderRadius: 0 }}
          >
            <iframe
              data-testid="spotify-embed-iframe"
              title="Randy Loveless on Spotify"
              src={embedUrl}
              width="100%"
              height="380"
              frameBorder="0"
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
              loading="lazy"
            />
          </div>

          <TrackClips />

          <div className="mt-5 flex flex-wrap gap-3">
            <a
              href={settings.spotify_artist_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="music-spotify-btn"
              className="inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-sm bg-[#B91C1C] hover:bg-[#991B1B] text-white px-6 py-3 brutal-shadow"
            >
              <Music2 size={16} /> Open on Spotify
              <ExternalLink size={12} />
            </a>
            {settings.apple_music_url && settings.apple_music_url !== "#" && (
              <a
                href={settings.apple_music_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-oswald uppercase tracking-[0.2em] text-sm border border-white/30 hover:border-white text-white px-6 py-3 transition-colors"
              >
                Apple Music <ExternalLink size={12} />
              </a>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
};
